import { ChangeDetectorRef, Component, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTable } from "@angular/material/table";
import { ActivatedRoute, Router } from "@angular/router";
import { NotificationService } from "src/app/notifications/notification.service";
import { SessionService } from "src/app/session.service";
import { AreYouSureDialogComponent } from "src/app/shared/dialogs/are-you-sure-dialog/are-you-sure-dialog.component";
import { FilterTableDialogComponent } from "src/app/shared/dialogs/filter-table-dialog/filter-table-dialog.component";
import { UiService } from "src/app/ui/ui.service";
import { pageSizeOptions } from "src/app/shared/utils/pagination";
import { BehaviorSubject, merge, Observable, of, Subject } from "rxjs";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  first,
  skip,
  switchMap,
  takeUntil,
  tap,
} from "rxjs/operators";
import { DataSource } from "@angular/cdk/table";
import { ElementLoaderDirective } from "src/app/ui/element-loader/element-loader.directive";
import { CollectionViewer } from "@angular/cdk/collections";
import { FormControl } from "@angular/forms";
import { IntegrationDTO } from "../Integration";
import { IntegrationsService } from "../integrations.service";
import { LocalStorageService } from "src/app/storage/local-storage.service";
@Component({
  selector: "app-integrations-list",
  templateUrl: "./integrations-list.component.html",
  styleUrls: ["./integrations-list.component.scss"],
})
export class IntegrationsListComponent implements OnInit {
  perPage: number = 20;
  pageSizeOptions = pageSizeOptions;
  searchValueControl = new FormControl("");
  integrations!: IntegrationDTO[];
  columns = [
    { code: "provider", name: "Provider", filter: true, selected: true },
    { code: "external_id", name: "External ID", filter: true, selected: true },
    { code: "options" },
  ];
  displayedColumns = this.columns.map((column) => column.code);

  @ViewChild("integrationsTable") integrationsTable!: MatTable<any[]>;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  hasIntegrationsWrite: boolean = false;
  hasIntegrationsDetail: boolean = false;
  private onComponentDestroy = new Subject();
  @ViewChild(ElementLoaderDirective)
  integrationElementLoader!: ElementLoaderDirective;
  integrationDataSource!: IntegrationsDataSource;
  onFilterColumnChange = new Subject();

  constructor(
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private router: Router,
    private IntegrationsService: IntegrationsService,
    private SessionService: SessionService,
    private NotificationService: NotificationService,
    private uiService: UiService,
    private ChangeDetectorRef: ChangeDetectorRef,
    private localStorageSvc: LocalStorageService,
  ) {}

  ngOnInit(): void {

    this.perPage = this.localStorageSvc.pageSize || 20;

    this.hasIntegrationsWrite =
      this.activatedRoute.snapshot.data.permissions.hasIntegrationsWrite;
    this.hasIntegrationsDetail =
      this.activatedRoute.snapshot.data.permissions.hasIntegrationsDetail;

  }

  ngAfterViewInit() {

    this.integrationDataSource = new IntegrationsDataSource(
      this.IntegrationsService,
      this.paginator,
      this.integrationElementLoader,
    );

    // Load initial data, then set up reactive loading
    this.loadIntegrationsPage().pipe(first()).subscribe(() => {
      // Set up reactive data loading for changes AFTER initial load is complete
      merge(
        this.sort.sortChange.pipe(tap(() => (this.paginator.pageIndex = 0))),
        this.paginator.page,
        this.onFilterColumnChange,
        this.searchValueControl.valueChanges.pipe(
          distinctUntilChanged(),
          tap(() => (this.paginator.pageIndex = 0)),
        ),
      )
        .pipe(
          debounceTime(300),
          switchMap(() => this.loadIntegrationsPage().pipe(first())),
          takeUntil(this.onComponentDestroy),
        )
        .subscribe();
    });

    this.ChangeDetectorRef.detectChanges();
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  get currentAccount() {
    return this.SessionService.currentAccountValue;
  }

  applyFilter() {
    // this.geofenceDataSource.filter = this.searchValue.trim().toLowerCase();
  }

  createNewIntegration() {
    this.router.navigate(["create"], {
      relativeTo: this.activatedRoute.parent,
    });
  }

  toggleFilterDialog() {
    this.dialog
      .open(FilterTableDialogComponent, {
        data: {
          columns: this.columns,
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((result) => {
        if (result) {
          this.columns = result;
          this.onFilterColumnChange.next();
        }
      });
  }

  isTripistProvider(element: any): boolean {
    const type = element?.provider?.type;
    return type === 'tripist_assistant' || type === 'explorist_assistant' || type === 'aura_assistant';
  }

  launchPreview(element: any): void {
    const providerType = element?.provider?.type;
    const featureTypeByProvider: Record<string, string> = {
      aura_assistant: 'aura',
      tripist_assistant: 'tripist',
      explorist_assistant: 'explorist',
    };
    const featureType = featureTypeByProvider[providerType];
    if (featureType) {
      this.router.navigate(['/assistist', featureType, element.id, 'preview']);
      return;
    }

    this.router.navigate([element.id, 'preview'], {
      relativeTo: this.activatedRoute.parent,
    });
  }

  edit(integration: IntegrationDTO) {
    this.router.navigate([integration.id], {
      relativeTo: this.activatedRoute.parent,
    });
  }



  delete(integration: IntegrationDTO) {
    this.dialog
      .open(AreYouSureDialogComponent, {
        data: {
          areYouSureInfo: {
            description: `Are you sure you want to delete this integration ( ${integration.id} )?`,
            warning: "Warning: this is not reversible!",
          },
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((result) => {
        if (result) {
          this.uiService
            .waitForEvent(
              this.IntegrationsService.deleteIntegration(
                this.currentAccount!.accountID,
                integration.id,
              ),
            )
            .pipe(takeUntil(this.onComponentDestroy))
            .subscribe(
              () => {
                this.NotificationService.openSuccessSnackBar(
                  "Integration deleted",
                );
                this.loadIntegrationsPage().pipe(first()).subscribe();
              },
              () => {
                this.NotificationService.openErrorSnackBar(
                  "Error deleting integration",
                );
              },
            );
        }
      });
  }

  loadIntegrationsPage() {
    const sort = this.buildSort();

    return this.integrationDataSource.loadIntegrations(
      this.SessionService.currentAccountValue!.accountID,
      this.SessionService.currentAppValue!.id,
      {
        page: this.paginator.pageIndex,
        perPage: this.paginator.pageSize,
        keyword: this.searchValueControl.value ?? "",
        forCount: true,
        sort,
        filter_columns: this.columns
          .filter((column) => column.selected)
          .map((column) => column.code),
      },
    );
  }

  buildSort() {
    const sort = [];
    if (this.sort.direction.length) {
      sort.push(`${this.sort.active}:${this.sort.direction}`);
    }
    return sort;
  }

  onPageChange(e: PageEvent){
    this.localStorageSvc.pageSize = e.pageSize;
    this.perPage = e.pageSize;
  }
}

export class IntegrationsDataSource implements DataSource<any> {
  private integrationsSubject = new BehaviorSubject<any[]>([]);

  constructor(
    private IntegrationsService: IntegrationsService,
    private paginator: MatPaginator,
    private integrationsElementLoader: ElementLoaderDirective,
  ) {}

  connect(collectionViewer: CollectionViewer): Observable<any[]> {
    return this.integrationsSubject.asObservable();
  }

  disconnect(collectionViewer: CollectionViewer) {
    this.integrationsSubject.complete();
  }

  loadIntegrations(masterAccountID: number, appID: number, queryParams: any) {
    return this.integrationsElementLoader
      .waitForEvent(
        this.IntegrationsService.getIntegrations(masterAccountID, appID, true).pipe(
          catchError(() => of({ count: 0, records: [] })),
        ),
      )
      .pipe(
        tap((filter: any) => {
          this.paginator.length = filter.count;
          const records = filter.records;

          this.integrationsSubject.next(records);
        }),
      );
  }
}
